import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, User, X } from 'lucide-react';
import { API, BACKEND_URL } from '../config/api';

export const playerPhotoSrc = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

export const resizeImage = (file, max = 400) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(img.src);
    resolve(canvas.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export const uploadPlayerPhoto = async (playerId, file, headers) => {
  const data = await resizeImage(file);
  return axios.put(`${API}/players/${playerId}/photo`, { photo_base64: data }, { headers });
};

export const PlayerAvatar = ({ player, size = 40, testId }) => {
  const [broken, setBroken] = useState(false);
  const src = playerPhotoSrc(player.photo_url);
  return (
    <span className="rounded-full overflow-hidden bg-slate-200 flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow-sm" style={{ width: size, height: size }} data-testid={testId}>
      {src && !broken
        ? <img src={src} alt={player.name} className="w-full h-full object-cover" loading="lazy" onError={() => setBroken(true)} />
        : <User size={size * 0.5} className="text-slate-400" />}
    </span>
  );
};

export const PlayerPhotoButton = ({ player, headers, onDone }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadPlayerPhoto(player.id, file, headers);
      toast.success('Η φωτογραφία ανέβηκε');
      onDone();
    } catch (e) { toast.error('Αποτυχία ανεβάσματος φωτογραφίας'); }
    setBusy(false);
  };
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Αφαίρεση φωτογραφίας;')) return;
    try {
      await axios.delete(`${API}/players/${player.id}/photo`, { headers });
      toast.success('Η φωτογραφία αφαιρέθηκε');
      onDone();
    } catch (err) { toast.error('Αποτυχία αφαίρεσης'); }
  };
  return (
    <div className="relative group">
      <button type="button" onClick={() => input.current.click()} className={`relative block ${busy ? 'opacity-50' : ''}`} title="Φωτογραφία παίκτριας" data-testid={`player-photo-btn-${player.id}`}>
        <PlayerAvatar player={player} size={40} />
        <span className="absolute -bottom-1 -right-1 bg-[#F97316] text-white rounded-full p-0.5"><Camera size={11} /></span>
      </button>
      {player.photo_url && (
        <button type="button" onClick={remove} className="absolute -top-1 -left-1 bg-red-500 text-white rounded-full p-0.5 opacity-80 hover:opacity-100" title="Αφαίρεση" data-testid={`player-photo-remove-${player.id}`}>
          <X size={10} />
        </button>
      )}
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={e => { pick(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
};
